import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, HelpCircle, Truck, CreditCard, Store, User, Utensils, Printer } from 'lucide-react';
import { Input } from '../components/ui/input';
import SEO from '../components/SEO';

export default function FAQPage() {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('ordering');
  const [openItems, setOpenItems] = useState({});

  const isSpanish = i18n.language === 'es';

  const categories = [
    { id: 'ordering', label: isSpanish ? 'Pedidos' : 'Ordering', icon: HelpCircle },
    { id: 'delivery', label: isSpanish ? 'Entrega' : 'Delivery', icon: Truck },
    { id: 'payment', label: isSpanish ? 'Pagos' : 'Payment', icon: CreditCard },
    { id: 'restaurants', label: isSpanish ? 'Restaurantes' : 'Restaurants', icon: Store },
    { id: 'restaurantOS', label: isSpanish ? 'Sistema de Restaurante' : 'Restaurant OS', icon: Utensils },
    { id: 'account', label: isSpanish ? 'Cuenta' : 'Account', icon: User },
  ];

  const faqs = {
    ordering: isSpanish ? [
      {
        q: '¿Cómo hago un pedido?',
        a: 'Simplemente explora los restaurantes en tu zona, selecciona los artículos que deseas, agrégalos a tu carrito y procede al pago. Puedes elegir entre entrega a domicilio, para llevar o comer en el local según el restaurante.'
      },
      {
        q: '¿Puedo programar un pedido para después?',
        a: 'Actualmente, todos los pedidos se realizan para preparación inmediata. Estamos trabajando en agregar pedidos programados en una actualización futura.'
      },
      {
        q: '¿Cómo aplico un código promocional?',
        a: 'Durante el pago, verás un campo para ingresar tu código promocional. Ingresa el código y haz clic en "Aplicar" para ver tu descuento reflejado en el total.'
      },
      {
        q: '¿Puedo modificar o cancelar mi pedido?',
        a: 'Una vez confirmado el pedido, se envía directamente al restaurante para su preparación. Por favor contáctanos inmediatamente si necesitas hacer cambios. La cancelación puede ser posible si el restaurante aún no ha comenzado a preparar tu comida.'
      },
      {
        q: '¿Qué pasa si mi pedido es incorrecto?',
        a: 'Si tu pedido está mal o faltan artículos, por favor contacta a nuestro equipo de soporte dentro de las 24 horas. Trabajaremos contigo y el restaurante para solucionarlo.'
      }
    ] : [
      {
        q: 'How do I place an order?',
        a: 'Simply browse restaurants in your area, select the items you want, add them to your cart, and proceed to checkout. You can choose delivery, takeaway, or dine-in options depending on the restaurant.'
      },
      {
        q: 'Can I schedule an order for later?',
        a: 'Currently, all orders are placed for immediate preparation. We are working on adding scheduled ordering in a future update.'
      },
      {
        q: 'How do I apply a promo code?',
        a: 'During checkout, you\'ll see a field to enter your promo code. Enter the code and click "Apply" to see your discount reflected in the total.'
      },
      {
        q: 'Can I modify or cancel my order?',
        a: 'Once an order is confirmed, it\'s sent directly to the restaurant for preparation. Please contact us immediately if you need to make changes. Cancellation may be possible if the restaurant hasn\'t started preparing your food.'
      },
      {
        q: 'What if my order is incorrect?',
        a: 'If your order is wrong or items are missing, please contact our support team within 24 hours. We\'ll work with you and the restaurant to make it right.'
      }
    ],
    delivery: isSpanish ? [
      {
        q: '¿Cuánto tarda la entrega?',
        a: 'Los tiempos de entrega típicamente van de 20 a 45 minutos dependiendo del tiempo de preparación del restaurante y tu distancia. Verás un tiempo estimado de entrega al pagar.'
      },
      {
        q: '¿Puedo rastrear mi pedido?',
        a: '¡Sí! Una vez que tu pedido es recogido, puedes rastrear a tu repartidor en tiempo real en la página de seguimiento del pedido. Recibirás actualizaciones en cada etapa de la entrega.'
      },
      {
        q: '¿Cuáles son los costos de entrega?',
        a: 'Los costos de entrega varían según el restaurante y la distancia. El costo exacto se muestra al pagar antes de realizar tu pedido. Algunos restaurantes ofrecen entrega gratis en pedidos superiores a cierta cantidad.'
      },
      {
        q: '¿Entregan a mi zona?',
        a: 'Ingresa tu dirección en nuestra página principal para ver los restaurantes disponibles que entregan a tu ubicación. La cobertura de entrega varía según el restaurante.'
      },
      {
        q: '¿Qué pasa si no estoy en casa cuando llega mi pedido?',
        a: 'Nuestros repartidores intentarán contactarte. Puedes agregar instrucciones de entrega durante el pago, como "dejar en la puerta" o proporcionar un número de contacto alternativo.'
      }
    ] : [
      {
        q: 'How long does delivery take?',
        a: 'Delivery times typically range from 20-45 minutes depending on the restaurant\'s preparation time and your distance. You\'ll see an estimated delivery time at checkout.'
      },
      {
        q: 'Can I track my order?',
        a: 'Yes! Once your order is picked up, you can track your driver in real-time on the order tracking page. You\'ll receive updates at each stage of delivery.'
      },
      {
        q: 'What are the delivery fees?',
        a: 'Delivery fees vary by restaurant and distance. The exact fee is shown at checkout before you place your order. Some restaurants offer free delivery on orders over a certain amount.'
      },
      {
        q: 'Do you deliver to my area?',
        a: 'Enter your address on our homepage to see available restaurants that deliver to your location. Delivery coverage varies by restaurant.'
      },
      {
        q: 'What if I\'m not home when my order arrives?',
        a: 'Our drivers will attempt to contact you. You can add delivery instructions during checkout, such as "leave at door" or provide an alternative contact number.'
      }
    ],
    payment: isSpanish ? [
      {
        q: '¿Qué métodos de pago aceptan?',
        a: 'Aceptamos todas las tarjetas de crédito y débito principales (Visa, Mastercard, American Express) a través de nuestro proveedor de pago seguro Stripe. Para pedidos para llevar y comer en el local, puedes pagar en el restaurante.'
      },
      {
        q: '¿Mi información de pago está segura?',
        a: 'Absolutamente. Usamos Stripe para el procesamiento de pagos, que cumple con PCI-DSS. Los detalles de tu tarjeta nunca se almacenan en nuestros servidores.'
      },
      {
        q: '¿Cuándo se me cobra por mi pedido?',
        a: 'Para pedidos de entrega a domicilio, tu tarjeta se cobra cuando realizas el pedido. Para llevar y comer en el local, el pago se hace en el restaurante.'
      },
      {
        q: '¿Cómo funcionan los reembolsos?',
        a: 'Los reembolsos se procesan de vuelta a tu método de pago original dentro de 5-10 días hábiles. Si tienes problemas con un pedido, contacta a nuestro equipo de soporte.'
      }
    ] : [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept all major credit and debit cards (Visa, Mastercard, American Express) through our secure payment provider Stripe. For takeaway and dine-in orders, you can pay at the restaurant.'
      },
      {
        q: 'Is my payment information secure?',
        a: 'Absolutely. We use Stripe for payment processing, which is PCI-DSS compliant. Your card details are never stored on our servers.'
      },
      {
        q: 'When am I charged for my order?',
        a: 'For delivery orders, your card is charged when you place the order. For takeaway and dine-in, payment is made at the restaurant.'
      },
      {
        q: 'How do refunds work?',
        a: 'Refunds are processed back to your original payment method within 5-10 business days. If you have issues with an order, contact our support team.'
      }
    ],
    restaurants: isSpanish ? [
      {
        q: '¿Cómo puedo asociar mi restaurante con Hambre?',
        a: 'Visita nuestra página "Asóciate con nosotros" o contacta a nuestro equipo de asociaciones de restaurantes. Te guiaremos a través del proceso de incorporación y te ayudaremos a configurar tu menú.'
      },
      {
        q: '¿Cuáles son las tarifas de comisión para restaurantes?',
        a: 'Las tarifas de comisión varían según los servicios que uses. Contacta a nuestro equipo de asociaciones para información detallada de precios adaptada a tu negocio.'
      },
      {
        q: '¿Puedo gestionar el menú de mi restaurante?',
        a: '¡Sí! Los socios restauranteros tienen acceso a un panel de gestión completo donde pueden actualizar su menú, precios, disponibilidad y horarios de atención.'
      },
      {
        q: '¿Cómo reporto un problema con un restaurante?',
        a: 'Puedes dejar una reseña después de tu pedido o contactar a nuestro equipo de soporte. Tomamos todos los comentarios seriamente y trabajamos con los restaurantes para mejorar el servicio.'
      }
    ] : [
      {
        q: 'How can I partner my restaurant with Hambre?',
        a: 'Visit our "Partner with us" page or contact our restaurant partnerships team. We\'ll guide you through the onboarding process and help set up your menu.'
      },
      {
        q: 'What are the commission rates for restaurants?',
        a: 'Commission rates vary based on the services you use. Contact our partnerships team for detailed pricing information tailored to your business.'
      },
      {
        q: 'Can I manage my restaurant\'s menu?',
        a: 'Yes! Restaurant partners have access to a full management dashboard where you can update your menu, prices, availability, and operating hours.'
      },
      {
        q: 'How do I report an issue with a restaurant?',
        a: 'You can leave a review after your order or contact our support team. We take all feedback seriously and work with restaurants to improve service.'
      }
    ],
    restaurantOS: isSpanish ? [
      {
        q: '¿Qué es Hambre OS?',
        a: 'Hambre OS es nuestro sistema operativo completo para restaurantes que incluye Punto de Venta (POS), Pantalla de Cocina (KDS), gestión de entregas, analíticas, gestión de inventario, programa de fidelidad y más - todo en una plataforma integrada.'
      },
      {
        q: '¿Qué hardware necesito?',
        a: 'Hambre OS funciona en cualquier dispositivo que ya tengas - tablets, teléfonos o computadoras. No se requieren terminales costosas ni hardware especializado.'
      },
      {
        q: '¿Cómo funciona el Punto de Venta?',
        a: 'Nuestro POS está optimizado para táctil y te permite procesar pedidos rápidamente, manejar pagos en efectivo y tarjeta, dividir cuentas, estacionar pedidos, gestionar turnos e imprimir recibos. Todo sincronizado con tu cocina y entregas.'
      },
      {
        q: '¿Qué es la Pantalla de Cocina (KDS)?',
        a: 'La Pantalla de Cocina muestra los pedidos en tiempo real a tu personal de cocina. Los pedidos se pueden enrutar a diferentes estaciones, incluyen alertas de temporizador, resaltan alergias y se pueden marcar como completados con un toque.'
      },
      {
        q: '¿Cómo funciona la gestión de inventario?',
        a: 'Puedes rastrear los niveles de stock de tus artículos del menú, establecer alertas de stock bajo y marcar automáticamente los artículos como no disponibles cuando se agotan. Esto se sincroniza en todos tus canales de ventas.'
      },
      {
        q: '¿Qué es el programa de fidelidad?',
        a: 'Puedes crear un sistema de puntos personalizado para recompensar a los clientes que regresan. Configura cuántos puntos ganan por libra gastada, puntos mínimos para canjear y bonos de bienvenida/cumpleaños.'
      },
      {
        q: '¿Puedo generar informes de fin de día?',
        a: 'Sí, los informes de fin de día muestran ventas totales, pedidos por tipo, métodos de pago, artículos más vendidos, propinas, anulaciones y más. Puedes imprimir o exportar estos informes.'
      },
      {
        q: '¿Qué impresoras son compatibles?',
        a: 'Hambre OS soporta impresoras térmicas a través de conexión de red (WebSocket), USB y Bluetooth. Puedes configurar impresoras separadas para recibos y tickets de cocina.'
      },
      {
        q: '¿Funciona sin conexión?',
        a: 'Sí, el sistema incluye modo sin conexión y sincronización de red local. Los dispositivos de meseros pueden sincronizarse con un POS principal incluso sin internet.'
      },
      {
        q: '¿Cuánto cuesta Hambre OS?',
        a: 'Ofrecemos planes desde £25/mes (con nuestro 50% de descuento especial). Hay una prueba gratuita de 14 días sin tarjeta de crédito requerida. Visita nuestra página de precios para más detalles.'
      }
    ] : [
      {
        q: 'What is Hambre OS?',
        a: 'Hambre OS is our complete restaurant operating system that includes Point of Sale (POS), Kitchen Display System (KDS), delivery management, analytics, inventory management, loyalty programs, and more - all in one integrated platform.'
      },
      {
        q: 'What hardware do I need?',
        a: 'Hambre OS works on any device you already own - tablets, phones, or computers. No expensive terminals or specialized hardware required.'
      },
      {
        q: 'How does the Point of Sale work?',
        a: 'Our POS is touch-optimized and lets you process orders quickly, handle cash and card payments, split bills, park orders, manage shifts, and print receipts. Everything syncs with your kitchen and delivery operations.'
      },
      {
        q: 'What is the Kitchen Display System (KDS)?',
        a: 'The Kitchen Display shows orders in real-time to your kitchen staff. Orders can be routed to different stations, include timer alerts, highlight allergies, and can be bumped as complete with one touch.'
      },
      {
        q: 'How does inventory management work?',
        a: 'You can track stock levels for your menu items, set low-stock alerts, and automatically mark items as unavailable when they run out. This syncs across all your sales channels.'
      },
      {
        q: 'What is the loyalty program?',
        a: 'You can create a custom points-based system to reward returning customers. Configure how many points they earn per pound spent, minimum points to redeem, and welcome/birthday bonuses.'
      },
      {
        q: 'Can I generate end-of-day reports?',
        a: 'Yes, end-of-day reports show total sales, orders by type, payment methods, top-selling items, tips, voids, and more. You can print or export these reports.'
      },
      {
        q: 'What printers are supported?',
        a: 'Hambre OS supports thermal printers via network (WebSocket), USB, and Bluetooth connections. You can configure separate printers for receipts and kitchen tickets.'
      },
      {
        q: 'Does it work offline?',
        a: 'Yes, the system includes offline mode and local network sync. Waiter devices can sync with a primary POS even without internet.'
      },
      {
        q: 'How much does Hambre OS cost?',
        a: 'We offer plans starting from £25/month (with our 50% off special offer). There\'s a 14-day free trial with no credit card required. Visit our pricing page for details.'
      }
    ],
    account: isSpanish ? [
      {
        q: '¿Cómo creo una cuenta?',
        a: 'Haz clic en "Registrarse" en la navegación superior, ingresa tus datos y verifica tu correo electrónico. También puedes registrarte rápidamente usando Google.'
      },
      {
        q: 'Olvidé mi contraseña. ¿Cómo la restablezco?',
        a: 'Haz clic en "¿Olvidaste tu contraseña?" en la página de inicio de sesión, ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.'
      },
      {
        q: '¿Cómo actualizo mi dirección de entrega?',
        a: 'Puedes ingresar una nueva dirección durante el pago, o guardar múltiples direcciones en la configuración de tu Perfil para selección rápida.'
      },
      {
        q: '¿Cómo elimino mi cuenta?',
        a: 'Contacta a nuestro equipo de soporte para solicitar la eliminación de tu cuenta. Ten en cuenta que esta acción es permanente y perderás tu historial de pedidos y preferencias guardadas.'
      },
      {
        q: '¿Está disponible la autenticación de dos factores?',
        a: '¡Sí! Puedes habilitar 2FA en tu Perfil > Configuración de Seguridad para mayor protección de tu cuenta.'
      }
    ] : [
      {
        q: 'How do I create an account?',
        a: 'Click "Sign Up" in the top navigation, enter your details, and verify your email. You can also sign up quickly using Google.'
      },
      {
        q: 'I forgot my password. How do I reset it?',
        a: 'Click "Forgot Password" on the login page, enter your email, and we\'ll send you a password reset link.'
      },
      {
        q: 'How do I update my delivery address?',
        a: 'You can enter a new address during checkout, or save multiple addresses in your Profile settings for quick selection.'
      },
      {
        q: 'How do I delete my account?',
        a: 'Contact our support team to request account deletion. Please note this action is permanent and you\'ll lose your order history and saved preferences.'
      },
      {
        q: 'Is two-factor authentication available?',
        a: 'Yes! You can enable 2FA in your Profile > Security settings for added account protection.'
      }
    ]
  };

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredFaqs = searchQuery 
    ? Object.values(faqs).flat().filter(faq => 
        faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs[activeCategory] || [];

  return (
    <>
      <SEO page="faq" />

      <div className="min-h-screen bg-slate-50 pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-orange-500 to-orange-600 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {isSpanish ? '¿Cómo podemos ayudarte?' : 'How can we help?'}
            </h1>
            <p className="text-xl text-orange-100 mb-8">
              {isSpanish ? 'Encuentra respuestas a preguntas frecuentes' : 'Find answers to frequently asked questions'}
            </p>
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={isSpanish ? 'Buscar respuestas...' : 'Search for answers...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg rounded-full border-0 shadow-lg"
              />
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {!searchQuery && (
            <div className="flex flex-wrap gap-3 mb-8 justify-center">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-medium transition-colors ${
                      activeCategory === cat.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-white text-slate-700 hover:bg-orange-50'
                    }`}
                    data-testid={`faq-category-${cat.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="max-w-3xl mx-auto">
            {searchQuery && (
              <p className="text-slate-600 mb-6">
                {filteredFaqs.length} {isSpanish ? 'resultados para' : 'results for'} "{searchQuery}"
              </p>
            )}

            <div className="space-y-4">
              {filteredFaqs.map((faq, index) => {
                const id = `${activeCategory}-${index}`;
                const isOpen = openItems[id];
                return (
                  <div 
                    key={id}
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                  >
                    <button
                      onClick={() => toggleItem(id)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`faq-item-${id}`}
                    >
                      <span className="font-medium text-slate-900 pr-4">{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredFaqs.length === 0 && (
              <div className="text-center py-12">
                <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">
                  {isSpanish ? 'No se encontraron resultados. Intenta con un término diferente.' : 'No results found. Try a different search term.'}
                </p>
              </div>
            )}
          </div>

          {/* Still need help */}
          <div className="mt-16 text-center">
            <p className="text-slate-600 mb-4">
              {isSpanish ? '¿Aún tienes preguntas?' : 'Still have questions?'}
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-full font-medium hover:bg-orange-600 transition-colors"
              data-testid="contact-support-btn"
            >
              {isSpanish ? 'Contactar Soporte' : 'Contact Support'}
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
